import React from 'react';
import { Box, Container, Divider, Paper, Stack, Typography } from '@mui/material';

const TermsOfService = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'left',
        }}
      >
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </Stack>

        <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
          These Terms of Service ("Terms") govern your access to and use of Lexory (the "Service"). By accessing or using the
          Service, you agree to these Terms.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Eligibility
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              You must be able to form a legally binding contract to use the Service. If you use the Service on behalf of an
              organization, you represent that you have authority to accept these Terms for that organization.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Accounts
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs
              under your account. You agree to provide accurate information and to keep it updated.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Acceptable use
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
              You agree not to misuse the Service. For example, you must not:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Use the Service for unlawful, harmful, or fraudulent purposes.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Attempt to reverse engineer, interfere with, or disrupt the Service.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Circumvent usage limits or access controls.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Upload content you do not have the right to use or share.
                </Typography>
              </li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Your content
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              You retain rights to the content you submit to the Service. You grant us a limited license to process that content
              solely to operate and provide the Service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Subscriptions and billing
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              Some features may require a paid subscription ("Premium"). Payments are processed by our payment provider. Pricing,
              billing cadence, and plan features may change over time.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Cancellations and refunds
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              You can cancel your subscription at any time. Unless required by law, payments are non-refundable and your
              subscription remains active until the end of the current billing period.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Intellectual property
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              The Service, including its software, design, and branding, is owned by Lexory and protected by intellectual property
              laws. These Terms do not grant you any rights to our trademarks or branding.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Disclaimers
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              The Service is provided on an "as is" and "as available" basis. We do not guarantee that the Service will be
              uninterrupted, error-free, or that translated output will be accurate or suitable for any specific purpose.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Limitation of liability
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              To the maximum extent permitted by law, Lexory will not be liable for any indirect, incidental, special,
              consequential, or punitive damages, or any loss of profits, data, or goodwill, arising out of or related to your use
              of the Service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Termination
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              We may suspend or terminate access to the Service if we reasonably believe you have violated these Terms or if
              necessary to protect the Service, users, or third parties.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Changes
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              We may update these Terms from time to time. The updated Terms will be posted on this page and apply going forward.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Contact
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              Questions about these Terms? Contact lexory@proton.me.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default TermsOfService;
